import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { toast } from "sonner";
import {
  Bell,
  ChatCircleText,
  ChatText,
  CheckCircle,
  CurrencyDollar,
  Lock,
  Sparkle,
  UsersThree,
  Package,
  Truck,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const KINDS = [
  ["all", "All"],
  ["lead", "Leads"],
  ["message", "Comments"],
  ["text", "Texts"],
  ["quote", "Quotes"],
  ["payment", "Payments"],
  ["hold", "Holds"],
  ["order", "Orders"],
  ["received", "Material in"],
  ["mention", "Mentions"],
];
const ICON = {
  mention: [ChatCircleText, "text-violet-300 bg-violet-500/15"],
  received: [Truck, "text-emerald-300 bg-emerald-500/15"],
  order: [Package, "text-red-300 bg-red-500/15"],
  lead: [UsersThree, "text-emerald-300 bg-emerald-500/15"],
  message: [ChatCircleText, "text-sky-300 bg-sky-500/15"],
  text: [ChatText, "text-fuchsia-300 bg-fuchsia-500/15"],
  quote: [Sparkle, "text-indigo-300 bg-indigo-500/15"],
  payment: [CurrencyDollar, "text-amber-300 bg-amber-500/15"],
  hold: [Lock, "text-orange-300 bg-orange-500/15"],
};

const ago = (iso) => {
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
};

// One poll shared by the bell and the dashboard widget.
export const useAlerts = (every = 60000) => {
  const [data, setData] = useState({ items: [], unread: 0, loaded: false });
  const load = useCallback(
    () =>
      api
        .get("/alerts")
        .then(({ data: d }) => setData({ items: d.items || [], unread: d.unread || 0, loaded: true }))
        .catch(() => setData((s) => ({ ...s, loaded: true }))),
    [],
  );
  useEffect(() => {
    load();
    const t = setInterval(load, every);
    return () => clearInterval(t);
  }, [load, every]);
  const markSeen = useCallback(async () => {
    await api.post("/alerts/seen");
    load();
  }, [load]);
  return { ...data, reload: load, markSeen };
};

export const AlertRow = ({ item, onOpen, onOrder, ordering, testid }) => {
  const [Icon, tone] = ICON[item.kind] || ICON.quote;
  const isOrder = item.kind === "order";
  return (
    <div
      className={`flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors duration-150 hover:border-indigo-400/40 hover:bg-zinc-900 ${
        item.unread ? "border-zinc-700 bg-zinc-950" : "border-zinc-800/70 bg-zinc-950/40"
      }`}
    >
      <button
        type="button"
        data-testid={testid}
        onClick={() => onOpen(item)}
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
      >
        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${tone}`}>
          <Icon size={15} weight="bold" />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block truncate text-sm ${item.unread ? "font-semibold text-zinc-100" : "text-zinc-300"}`}>
            {item.title}
          </span>
          {item.detail && <span className="mt-0.5 line-clamp-2 block text-xs text-zinc-500">{item.detail}</span>}
          <span className="mt-1 block text-[10px] uppercase tracking-widest text-zinc-600">{ago(item.at)}</span>
        </span>
      </button>
      {isOrder &&
        (item.ordered ? (
          <button
            type="button"
            data-testid={`alert-ordered-${item.order_id}`}
            onClick={() => onOpen(item)}
            className="mt-0.5 shrink-0 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest text-emerald-300 transition-colors duration-150 hover:bg-emerald-500/20"
          >
            {item.po_code ? `Ordered · ${item.po_code}` : "Ordered"}
          </button>
        ) : onOrder ? (
          <button
            type="button"
            disabled={ordering}
            data-testid={`alert-order-${item.order_id}`}
            onClick={() => onOrder(item)}
            className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-indigo-400/40 bg-indigo-500/15 px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500 hover:text-white disabled:opacity-40"
          >
            <Package size={11} weight="bold" /> Order it
          </button>
        ) : null)}
      {item.unread && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-red-400" aria-hidden="true" />}
    </div>
  );
};

// One-click: turn an "Order needed" alert into a draft PO, then open it.
const useOrderFromAlert = (reload) => {
  const navigate = useNavigate();
  const [ordering, setOrdering] = useState(false);
  const orderFromAlert = useCallback(
    async (item) => {
      if (!item?.order_id) return;
      setOrdering(true);
      try {
        const { data } = await api.post(`/order-alerts/${item.order_id}/purchase-order`);
        toast.success(`${data.po_code} drafted — review and send`);
        reload?.();
        navigate(`/inventory/purchase-orders/${data.po_id}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not create the purchase order");
      } finally {
        setOrdering(false);
      }
    },
    [navigate, reload],
  );
  return { orderFromAlert, ordering };
};

/** Bell in the top bar: red unread count, dropdown of recent alerts, mark all read. */
export const AlertsBell = () => {
  const navigate = useNavigate();
  const { items, unread, loaded, markSeen, reload } = useAlerts();
  const { orderFromAlert, ordering } = useOrderFromAlert(reload);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("all");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const shown = useMemo(() => items.filter((i) => kind === "all" || i.kind === kind), [items, kind]);
  const counts = useMemo(() => {
    const c = {};
    items.forEach((i) => {
      if (i.unread) c[i.kind] = (c[i.kind] || 0) + 1;
    });
    return c;
  }, [items]);

  const openItem = (item) => {
    setOpen(false);
    markSeen();
    navigate(item.href || "/");
  };

  return (
    <div ref={boxRef} className="relative shrink-0">
      <button
        type="button"
        data-testid="alerts-bell"
        onClick={() => setOpen((v) => !v)}
        title="Alerts"
        aria-label={`Alerts${unread ? `, ${unread} unread` : ""}`}
        aria-expanded={open}
        className={`relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-150 ${
          open || unread
            ? "border-indigo-400/40 bg-indigo-500/10 text-indigo-200"
            : "border-zinc-800 text-zinc-500 hover:border-indigo-400/40 hover:text-indigo-300"
        }`}
      >
        <Bell size={17} weight={unread ? "fill" : "bold"} />
        {unread > 0 && (
          <span
            data-testid="alerts-badge"
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-black leading-none text-white shadow-[0_0_0_2px_#09090b]"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="alerts-popover"
          className="absolute right-0 top-11 z-[90] w-[min(92vw,26rem)] overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]"
        >
          <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
            <Bell size={15} weight="fill" className="text-indigo-300" />
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-200">Alerts</span>
            <span className="font-mono-data text-[10px] text-zinc-500" data-testid="alerts-unread-text">
              {unread ? `${unread} new` : loaded ? "All caught up" : "Loading…"}
            </span>
            <button
              type="button"
              data-testid="alerts-mark-read"
              disabled={!unread}
              onClick={markSeen}
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-400 hover:text-indigo-200 disabled:opacity-40"
            >
              <CheckCircle size={12} weight="bold" /> Mark all read
            </button>
          </div>
          <div className="flex gap-1 overflow-x-auto border-b border-zinc-900 px-3 py-2" data-testid="alerts-kinds">
            {KINDS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-testid={`alerts-kind-${k}`}
                onClick={() => setKind(k)}
                className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  kind === k ? "bg-indigo-500 text-white" : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                }`}
              >
                {label}
                {k !== "all" && counts[k] ? (
                  <span className={`rounded-full px-1.5 text-[9px] ${kind === k ? "bg-white/20" : "bg-red-500/80 text-white"}`}>
                    {counts[k]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          <div className="max-h-[60vh] space-y-1.5 overflow-y-auto p-3" data-testid="alerts-list">
            {shown.length === 0 ? (
              <p className="px-2 py-8 text-center text-sm text-zinc-500" data-testid="alerts-empty">
                {loaded ? "Nothing here from the last 14 days." : "Loading alerts…"}
              </p>
            ) : (
              shown.map((it) => <AlertRow key={it.id} item={it} onOpen={openItem} onOrder={orderFromAlert} ordering={ordering} testid={`alert-${it.id}`} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Dashboard card: the five newest alerts. */
export const AlertsWidget = () => {
  const navigate = useNavigate();
  const { items, unread, loaded, markSeen, reload } = useAlerts(90000);
  const { orderFromAlert, ordering } = useOrderFromAlert(reload);
  const latest = items.slice(0, 5);
  return (
    <section data-testid="dash-alerts" className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:p-5">
      <div className="flex items-center gap-2">
        <Bell size={16} weight="fill" className={unread ? "text-red-400" : "text-zinc-500"} />
        <h2 className="text-xs uppercase tracking-widest text-zinc-500">Latest alerts</h2>
        {unread > 0 && (
          <span className="font-mono-data rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-black text-white" data-testid="dash-alerts-unread">
            {unread > 99 ? "99+" : unread} new
          </span>
        )}
        {unread > 0 && (
          <button
            type="button"
            data-testid="dash-alerts-mark-read"
            onClick={markSeen}
            className="ml-auto text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:text-indigo-200"
          >
            Mark all read
          </button>
        )}
      </div>
      {latest.length === 0 ? (
        <p className="mt-3 text-sm text-zinc-500" data-testid="dash-alerts-empty">
          {loaded ? "No leads, comments, texts or payments in the last 14 days." : "Loading…"}
        </p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {latest.map((it) => (
            <AlertRow key={it.id} item={it} onOpen={(i) => { markSeen(); navigate(i.href || "/"); }} onOrder={orderFromAlert} ordering={ordering} testid={`dash-alert-${it.id}`} />
          ))}
        </div>
      )}
      {items.length > 5 && (
        <Link
          to="/leads"
          data-testid="dash-alerts-more"
          className="mt-3 inline-block text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-indigo-200"
        >
          {items.length - 5} more in the bell ↗
        </Link>
      )}
    </section>
  );
};
